import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  FolderOpen as FolderOpenIcon,
  GetApp as DownloadIcon,
  InsertDriveFile as FileIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon,
  RateReview as RateReviewIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CharCounter from "../components/CharCounter";
import AutorizacaoStatusChip from "../components/AutorizacaoStatusChip";
import PagamentosSection, { PAPEL_ANALISTA, PAPEL_SECRETARIO, PAPEL_EMPREENDEDOR } from "../components/PagamentosSection";
import { AuthContext } from "../contexts/AuthContext";
import autorizacoesService from "../services/autorizacoes";
import {
  statusAutorizacaoLabels,
  tipoAutorizacaoEnum,
} from "../utils/constants";
import { formatDateToShow } from "../utils/formatters";

const OBS_MAX = 1000;

const tipoLabel = (tipo) =>
  tipoAutorizacaoEnum.find((t) => t.value === tipo)?.label ?? "—";

const statusOptions = Object.entries(statusAutorizacaoLabels).map(
  ([value, label]) => ({ value: Number(value), label })
);

function DetalheAutorizacao() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const isAnalista = !!user?.data?.isAnalista;
  const isSecretario = !!user?.data?.isSecretario;

  const [solicitacao, setSolicitacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [novoStatus, setNovoStatus] = useState("");
  const [observacoesAnalise, setObservacoesAnalise] = useState("");
  const [saving, setSaving] = useState(false);

  const [documentos, setDocumentos] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docError, setDocError] = useState("");
  const [autorizacaoEmitida, setAutorizacaoEmitida] = useState(null);

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await autorizacoesService.getById(id);
      setSolicitacao(data);
      setNovoStatus(data?.status ?? "");
    } catch (err) {
      setError(err?.message || "Erro ao carregar autorização.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const carregarDocumentos = useCallback(async () => {
    try {
      setLoadingDocs(true);
      setDocError("");
      const docs = await autorizacoesService.listarDocumentosAutorizacao(id);
      setDocumentos(docs);
    } catch (err) {
      setDocError(err?.message || "Erro ao carregar documentos.");
    } finally {
      setLoadingDocs(false);
    }
  }, [id]);

  const carregarAutorizacaoEmitida = useCallback(async () => {
    try {
      const doc = await autorizacoesService.obterAutorizacaoEmitida(id);
      setAutorizacaoEmitida(doc);
    } catch {
    }
  }, [id]);

  useEffect(() => {
    carregar();
    carregarDocumentos();
    carregarAutorizacaoEmitida();
  }, [carregar, carregarDocumentos, carregarAutorizacaoEmitida]);

  const handleAtualizarStatus = async () => {
    if (novoStatus === "" || novoStatus === solicitacao.status) {
      setError("Selecione um status diferente do atual.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await autorizacoesService.atualizarStatus({
        id,
        novoStatus: Number(novoStatus),
        observacoes: observacoesAnalise || null,
      });
      setSuccess("Status atualizado com sucesso.");
      setObservacoesAnalise("");
      await carregar();
    } catch (err) {
      setError(err?.message || "Erro ao atualizar status.");
    } finally {
      setSaving(false);
    }
  };

  const handleVisualizarDocumento = (doc) => {
    try {
      const bytes = Uint8Array.from(atob(doc.conteudo), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: doc.contextType || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      window.alert("Não foi possível abrir o documento.");
    }
  };

  const handleBaixarLicenca = () => {
    try {
      const base64 = autorizacaoEmitida?.fileContent || autorizacaoEmitida?.conteudo;
      if (!base64) return;
      const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      window.alert("Não foi possível baixar a autorização.");
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!solicitacao) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
        <Alert severity="error">
          {error || "Autorização não encontrada."}
        </Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Hero banner */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label="Voltar"
            sx={{
              backgroundColor: theme.palette.success.main,
              color: "success.contrastText",
              width: 44,
              height: 44,
              flexShrink: 0,
              "&:hover": { backgroundColor: theme.palette.success.dark },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.primary.main,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              {tipoLabel(solicitacao.tipoAutorizacao)}
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                mt: 0.25,
                mb: 1,
                lineHeight: 1.2,
                fontSize: { xs: "1.1rem", sm: "1.35rem", md: "1.5rem" },
              }}
            >
              {solicitacao.nomeEvento}
            </Typography>
            <AutorizacaoStatusChip status={solicitacao.status} />
          </Box>
          {autorizacaoEmitida && (
            <Button
              variant="contained"
              color="success"
              size="medium"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleBaixarLicenca}
              sx={{
                ml: { md: "auto" },
                flexShrink: 0,
                alignSelf: { xs: "stretch", md: "center" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Baixar Licença
            </Button>
          )}
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: solicitacao.observacoes ? 2 : 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Data do Evento
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatDateToShow(solicitacao.dataEvento)}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <LocationIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Local
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {solicitacao.localEvento}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
              height: "100%",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <CalendarIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Solicitado em
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatDateToShow(solicitacao.createAt)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {solicitacao.observacoes && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DescriptionIcon sx={{ fontSize: 18, color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Observações
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {solicitacao.observacoes}
          </Typography>
        </Paper>
      )}

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <FolderOpenIcon sx={{ color: theme.palette.primary.main }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
            Documentos Anexados
          </Typography>
        </Box>

        {docError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDocError("")}>
            {docError}
          </Alert>
        )}

        {loadingDocs ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Carregando documentos...
            </Typography>
          </Box>
        ) : documentos.length === 0 ? (
          <Box
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: alpha(theme.palette.divider, 0.04),
              border: `1px dashed ${alpha(theme.palette.divider, 0.4)}`,
              borderRadius: 2,
            }}
          >
            <FileIcon sx={{ fontSize: 36, color: alpha(theme.palette.text.secondary, 0.3), mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Nenhum documento enviado.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {documentos.map((doc) => (
              <Grid item xs={12} sm={6} md={4} key={doc.id}>
                <Card
                  elevation={0}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                    borderRadius: 2,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                    },
                  }}
                >
                  <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FileIcon sx={{ color: theme.palette.primary.main, fontSize: 22, flexShrink: 0 }} />
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {doc.fileName}
                        </Typography>
                        {doc.descricao && doc.descricao !== doc.fileName && (
                          <Typography variant="caption" color="text.secondary">
                            {doc.descricao}
                          </Typography>
                        )}
                      </Box>
                      {doc.conteudo && (
                        <Tooltip title="Visualizar documento">
                          <IconButton
                            size="small"
                            aria-label="Visualizar documento"
                            onClick={() => handleVisualizarDocumento(doc)}
                            sx={{
                              color: theme.palette.primary.main,
                              flexShrink: 0,
                              "&:hover": {
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {solicitacao && (
        <PagamentosSection
          solicitacao={solicitacao}
          papel={isAnalista ? PAPEL_ANALISTA : isSecretario ? PAPEL_SECRETARIO : PAPEL_EMPREENDEDOR}
          variant="paper"
        />
      )}

      {(isAnalista || isSecretario) && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
            backgroundColor: alpha(theme.palette.warning.main, 0.03),
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
            <RateReviewIcon sx={{ color: theme.palette.warning.dark }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme.palette.warning.dark,
                fontSize: { xs: "1rem", md: "1.25rem" },
              }}
            >
              Painel de Análise
            </Typography>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Novo Status</InputLabel>
                <Select
                  value={novoStatus}
                  label="Novo Status"
                  onChange={(e) => setNovoStatus(e.target.value)}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Observações da Análise"
                value={observacoesAnalise}
                inputProps={{ maxLength: OBS_MAX }}
                onChange={(e) => setObservacoesAnalise(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <CharCounter value={observacoesAnalise} maxLength={OBS_MAX} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ justifyContent: "flex-end", alignItems: "stretch" }}
              >
              {Number(solicitacao.status) === 3 && (
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => navigate(`/autorizacao/${id}/emitir`)}
                  fullWidth
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    minWidth: { sm: 200 },
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  Emitir Autorização
                </Button>
              )}
              <Button
                variant="contained"
                onClick={handleAtualizarStatus}
                disabled={saving || novoStatus === solicitacao.status}
                fullWidth
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: { sm: 160 },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {saving ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Atualizar Status"
                )}
              </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
}

export default DetalheAutorizacao;
